/*
 * @Author: Ryan
 * @Date: 2026-02-03
 * @Description: Why choose FalcoCut AI Image Enhancer section with 6 feature cards
 */
import React from 'react';

interface FeatureCard {
  icon: string;
  title: string;
  description: string;
}

const features: FeatureCard[] = [
  {
    icon: '✨',
    title: 'Simple by Design',
    description:
      'The interface is easy for anyone to use, with no technical skills needed. Upload a photo and the AI handles the enhancement from start to finish.',
  },
  {
    icon: '⚡️',
    title: 'Lightning-Fast Processing',
    description:
      "Results appear in seconds thanks to an optimized AI engine that analyzes and improves images quickly. Whether you're working on one photo or many, you get clear upgrades without waiting.",
  },
  {
    icon: '🖼️',
    title: 'True-to-Life HD Quality',
    description:
      'Resolution and detail are enhanced while keeping colors balanced and edges smooth. Your images stay natural and realistic, ready for printing, sharing, or professional use.',
  },
  {
    icon: '🔒',
    title: 'Secure and Reliable',
    description:
      'Images are processed with strong security measures and handled with care. No unnecessary data is stored, so your content stays protected throughout the workflow.',
  },
  {
    icon: '🔧',
    title: 'Corrects Distortions Effortlessly',
    description:
      'Blur, pixelation, and compression artifacts are repaired automatically to restore clarity. Even shaky shots or low-res uploads become clean, usable images with minimal effort.',
  },
  {
    icon: '🌐',
    title: 'Works Anywhere You Do',
    description:
      'FalcoCut runs smoothly on desktop, tablet, or mobile, all inside your browser. No downloads are needed, making it easy to enhance photos wherever you are.',
  },
];

const FeatureCardItem: React.FC<FeatureCard> = ({ icon, title, description }) => {
  return (
    <div className="bg-white rounded-2xl p-6 pad:p-8 shadow-sm border border-gray-100">
      <div className="text-3xl pad:text-4xl mb-3 pad:mb-4">{icon}</div>
      <h3 className="text-lg pad:text-xl font-semibold text-gray-900 mb-2 pad:mb-3">
        {title}
      </h3>
      <p className="text-sm pad:text-base text-gray-600 leading-relaxed">{description}</p>
    </div>
  );
};

const WhyChooseSection: React.FC = () => {
  return (
    <section className="w-full bg-[#F5F7FB] py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* Title */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-4 pad:mb-5 leading-tight">
          Why Choose FalcoCut AI Image Enhancer
        </h2>

        {/* Description */}
        <p className="text-sm pad:text-base sm:text-base text-center text-gray-600 max-w-4xl mx-auto mb-10 pad:mb-14 sm:mb-16 leading-relaxed">
          FalcoCut streamlines image enhancement with fast, reliable AI that produces
          natural results and keeps your workflow easy. You can upgrade any image in just
          a few clicks and move on with your project.
        </p>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 pad:grid-cols-2 gap-4 pad:gap-6">
          {features.map((feature, index) => (
            <FeatureCardItem
              key={index}
              icon={feature.icon}
              title={feature.title}
              description={feature.description}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyChooseSection;
